import { defineStore, storeToRefs } from "pinia"
import { ref, computed, inject } from "vue"
import { useFetchWrapper } from "@core/composables"
import type {
    Source,
    WorkflowEvent,
    ComparatorAnalysis,
    ComparatorOutput,
    Denivele,
    ComparatorInputDeniveleCourtage,
    ComparatorInputPricerGlobal,
    ComparatorInputOffreComparee,
    ComparatorInputOffreActuelle,
    ComparatorInputPricerGetData,
    ComparatorInput,
    ComparatorOutputWarning,
} from "@types"
import { PeriodAvailableResponse, Services, SuppliersResponse } from "@services"
import moment from "moment"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useAuthStore } from "@core/stores/auth.store"
import { OptionTarifaire } from "@modules/courtage/constants"
import { ServiceError } from "@core/exceptions"

/** Libellés des étapes workflow (ex-`useI18n` local) — évite `useI18n` dans le store (incompatible hors setup / après await). */
const WORKFLOW_STEP_LABELS_FR: Record<string, string> = {
    sign_content: "Consentement",
    data_technical: "Données techniques et contractuelles",
    data_daily: "Données journalières",
    sample_hourly: "Échantillon courbe de charge",
    learning: "Apprentissage sur le compteur",
    data_hourly: "Collecte de l'historique de la courbe",
}

/**
 * Store permettant de gérer les interactions et données utilisées
 * dans la page de l'analyse de comparaison d'offres
 */
export const useComparatorStore = defineStore("comparator", () => {
    const buildingStore = useBuildingStore()
    const { building: buildingSelected } = storeToRefs(buildingStore)

    const authStore = useAuthStore()
    const { userId, companyId } = storeToRefs(authStore)

    // ==================
    // STATE
    // ==================

    /**
     * Liste des sources PDL du bâtiment
     */
    const sources = ref<Source[]>([])

    /**
     * Identifiant de la source sélectionnée
     */
    const sourceSelected = ref<string>("")

    /**
     * CEE sécurité
     */
    const ceeSecurite = ref<number>(0.99)

    /**
     * Consommation CEE
     */
    const consommationCee = ref<number>(0)

    /**
     * Consommation totale annuelle
     */
    const consommationTotalAnnuelle = ref<number | null>(0)

    /**
     * Code NAF de l'entreprise
     */
    const codeNaf = ref<string>("")

    /**
     * Périodes disponibles pour la source sélectionnée
     */
    const periodsAvailable = ref<
        PeriodAvailableResponse["data"]["availability"]["periodes"]
    >([])
    /**
     * Meilleure période disponible
     */
    const bestPeriodAvailable = ref<
        PeriodAvailableResponse["data"]["availability"]["best_periode"] | null
    >(null)

    /**
     * Période sélectionnée pour la source sélectionnée
     */
    const periodSelected = ref<
        PeriodAvailableResponse["data"]["availability"]["best_periode"] | null
    >(null)

    /**
     * Mode de sélection de la période
     */
    const modePeriod = ref<"best" | "custom">("best")

    /**
     * Liste des fournisseurs d'énergies
     */
    const suppliers = ref<SuppliersResponse>([])

    /**
     * Graphique de la source sélectionnée
     */
    const graphJson = ref<{
        _id_source: string
        json: {
            data: any[]
            layout: any
            config: any
        }
    } | null>(null)

    /**
     * Résultat de l'analyse de comparaison d'offres non sauvegardée
     */
    const currentAnalysis = ref<ComparatorOutput | null>(null)

    /**
     * Liste des warnings générés lors de la comparaison des offres
     */
    const warnings = ref<ComparatorOutputWarning[]>([])

    /**
     * Liste des analyses sauvegardées
     */
    const analyses = ref<ComparatorAnalysis[]>([])

    /**
     * Offre actuelle renseignée
     */
    const offreActuelle = ref<ComparatorInputOffreActuelle | null>(null)

    /**
     * Offres alternatives renseignées
     */
    const offresComparees = ref<ComparatorInputOffreComparee[]>([])

    /**
     * Consommation par plage fournisseur
     */
    const consommationByPlageFournisseur = ref<{ [name: string]: number }>({})

    /**
     * Snapshot de la consommation par plage fournisseur pour vérifier les modifications
     */
    const snapshotConsommationByPlageFournisseur = ref<{
        [name: string]: number
    }>({})

    /**
     * Indique si on utilise la consommation par poste électrique
     */
    const useEnergieparposteElec = ref<boolean>(false)

    // ==================
    // GETTERS
    // ==================

    function computedEligible(source: Source & { eligible?: boolean }) {
        source.eligible = true

        if (source.informations?.pdl?.compteur?.teleoperable === false) {
            source.eligible = false
            return source
        }

        const status =
            source.informations?.pdl?.data_retrieval?.historical?.state

        const dateFrom =
            source.informations?.pdl?.data?.soutirage?.cdc?.date_from

        if (!dateFrom) {
            source.eligible = false
            return source
        }

        // Validation du workflow
        if (status !== "finished") {
            source.eligible = false
            return source
        }

        // Validation de l'historique des données (minimum 1 an)
        if (dateFrom) {
            const limit = moment(new Date()).subtract(1, "year").toDate()

            if (moment(dateFrom).isAfter(limit)) {
                source.eligible = false
                return source
            }
        }

        return source
    }

    /**
     * Récupère les sources avec le statut d'éligibilité
     */
    const getSourcesWithEligible = computed(() => {
        return sources.value.map((source) => computedEligible(source))
    })

    /**
     * Récupère le détail de la source sélectionnée
     */
    const getSourceById = computed(() => {
        return getSourcesWithEligible.value.find(
            (source: Source) => source._id_source === sourceSelected.value
        )
    })

    /**
     * Récupère le dénivelé de puissance formaté pour l'API Courtage
     */
    const getDenivelePuissanceFormated = computed(() => {
        return mappingDenivelle(
            getSourceById.value?.informations?.pdl?.structure_tarifaire
                ?.puissance_souscrite_soutirage?.denivele ?? null
        )
    })

    /**
     * Récupère le dénivelé de puissance brut
     */
    const getDenivelePuissance = computed(() => {
        return (
            getSourceById.value?.informations?.pdl?.structure_tarifaire
                ?.puissance_souscrite_soutirage?.denivele ?? null
        )
    })

    /**
     * Récupère la puissance obligatoire de capacité
     * - Si C5 ou P4 : puissance souscrite
     * - Sinon : max dénivelé de puissance
     */
    const getPuissanceOc = computed(() => {
        // Dans le cas d'un compteur C5, P4 on regarde la puissance souscrite
        if (
            ["C5", "P4"].includes(
                getSourceById.value?.informations?.pdl?.compteur?.segment ??
                    "UNDEFINED"
            )
        ) {
            const puissanceSouscrite =
                getSourceById.value?.informations?.pdl?.structure_tarifaire
                    ?.puissance_souscrite_soutirage?.puissance

            return puissanceSouscrite
        }

        const maxDenivele = Math.max(
            ...(Object.values(getDenivelePuissance.value ?? {}).filter((v) =>
                Number.isFinite(v)
            ) as number[])
        )

        // Retourne la valeur max du dénivelé de puissance ou la valeur calculée
        return maxDenivele
    })

    const getCurrentOptionTarifaire = computed(() => {
        const segment =
            getSourceById.value?.informations?.pdl?.compteur?.segment ??
            "UNDEFINED"

        if (segment === "UNDEFINED") return "—"

        // récupère les heures creuses
        const heures_creuses =
            getSourceById.value?.informations?.pdl?.comptage?.plages_horaires
                ?.heures_creuses ?? []
        const heures_pointes_fixe =
            getSourceById.value?.informations?.pdl?.comptage?.plages_horaires
                ?.heures_pointe_fixe ?? []

        if (!heures_creuses.length) {
            return OptionTarifaire.Base
        }

        // Si le segment est C5, P4 et qu'il y a des heures creuses
        if (["C5", "P4"].includes(segment) && heures_creuses.length) {
            return OptionTarifaire.HPHC
        }

        // Si le segment n'est pas C5, P4 et qu'il y a des heures creuses
        if (!["C5", "P4"].includes(segment) && heures_creuses.length) {
            return heures_pointes_fixe.length
                ? OptionTarifaire.HS_PTE_Fixe
                : OptionTarifaire.HS
        }

        return null
    })

    /**
     * Récupère les étapes du workflow de la source sélectionnée
     */
    const getWorkflowEventsBySourceId = computed(() => {
        const steps_names = [
            "sign_content",
            "data_technical",
            "data_daily",
            "sample_hourly",
            "learning",
            "data_hourly",
        ]

        const steps: WorkflowEvent[] = steps_names.map((step) => ({
            step,
            name: WORKFLOW_STEP_LABELS_FR[step] ?? step,
            status: 0,
        }))

        if (!getSourceById.value) {
            return steps
        }

        // On met à jour les étapes en fonction de la source

        // récupère l'état du workflow
        const state =
            getSourceById.value.informations?.pdl?.data_retrieval?.historical
                ?.state ?? "created"

        // En fonction de l'état du workflow, on met à jour les étapes
        switch (state) {
            case "noconsent":
                // Passe toutes les étapes à "En attente"
                steps.forEach((step) => {
                    step.status = 0
                })
                break
            // Si le workflow est terminé, on met à jour toutes les étapes à "En terminé"
            case "finished":
                // Passe toutes les étapes à "En terminé"
                steps.forEach((step) => {
                    step.status = 2
                })
                break
            // Si le workflow est en erreur, on met toutes les étapes à "En erreur"
            case "crashed":
                // Passe toutes les étapes à "En erreur"
                steps.forEach((step) => {
                    step.status = 3
                })
                break
            // En attente de la courbe de charge
            case "delayed":
            case "waiting":
                // Toute les étapes sauf la dernière sont terminées
                steps.forEach((step) => {
                    if (step.step !== "data_hourly") {
                        step.status = 2
                    } else step.status = 1
                })
                break
            case "runnable":
            case "running":
                // Passe toutes les étapes à "En attente"
                steps.forEach((step) => {
                    step.status = 1
                })
                break
            // Pour tous les autres états, on met à jour les étapes à "En cours" sauf la dernière
            default:
                steps.forEach((step) => {
                    if (step.step !== "data_hourly") {
                        step.status = 1
                    }
                })
                break
        }

        // Si la source a un consentement signé, on met à jour l'étape "sign_content"
        if (
            getSourceById.value.informations?.pdl?.consentement.etat ===
            "active"
        ) {
            steps[0].status = 4
        }
        // Si la source n'a pas de consentement signé, on met à jour l'étape "sign_content" et on retourne le workflow
        else {
            steps[0].status = 1
            return steps
        }

        return steps
    })

    // ==================
    // ACTIONS SYNCHRONES
    // ==================

    /**
     * Réinitialise le store à son état initial.
     */
    function $reset() {
        sources.value = []
        sourceSelected.value = ""
        ceeSecurite.value = 0.99
        consommationCee.value = 0
        consommationTotalAnnuelle.value = 0
        codeNaf.value = ""
        periodsAvailable.value = []
        bestPeriodAvailable.value = null
        periodSelected.value = null
        modePeriod.value = "best"
        currentAnalysis.value = null
        warnings.value = []
        consommationByPlageFournisseur.value = {}
        snapshotConsommationByPlageFournisseur.value = {}
        useEnergieparposteElec.value = false
        graphJson.value = null
        analyses.value = []
        offreActuelle.value = null
        offresComparees.value = []
        suppliers.value = [] as SuppliersResponse
    }

    // ==================
    // ACTIONS ASYNCHRONES
    // ==================

    /**
     * Injection des services
     */
    const services = inject<Services>("services")

    /**
     * Permet de mapper le dénivelé de puissance du format Base de donnée au format attendu par l'API Courtage.
     * Les classes temporelles HPE et HCE deviennent HPB et HCB.
     * @param denivele - Dénivelé de puissance au format Base de donnée
     * @returns Le dénivelé de puissance au format attendu par l'API Courtage
     */
    function mappingDenivelle(
        denivele: Denivele | null
    ): ComparatorInputDeniveleCourtage | null {
        if (!denivele) return null

        const denivelePricer = {
            HPB: denivele?.HPE ?? 0,
            HCB: denivele?.HCE ?? 0,
            HPH: denivele?.HPH ?? 0,
            HCH: denivele?.HCH ?? 0,
            PTE: denivele?.PTE ?? undefined,
        }

        // Filtre les valeurs undefined
        return Object.fromEntries(
            Object.entries(denivelePricer).filter(
                ([, value]) => value !== undefined
            )
        ) as ComparatorInputDeniveleCourtage
    }

    /**
     * Utilitaire pour construire le body de l'API createCompareOffers & createCompareOffersAnalysis
     * @returns
     */
    function getBodyInputCompareOffers(): ComparatorInput {
        if (!sourceSelected.value) {
            throw new Error("Aucune source sélectionnée")
        }

        if (!periodSelected.value && modePeriod.value === "custom") {
            throw new ServiceError({
                message:
                    "Aucune période de consommation de référence sélectionnée",
                code: "no_period_selected",
            })
        }
        if (!bestPeriodAvailable.value && modePeriod.value === "best") {
            throw new ServiceError({
                message:
                    "Aucune période de consommation de référence sélectionnée",
                code: "no_period_selected",
            })
        }

        // Build pricer_global as InputPricerGlobal (Omit<Pricer, "structure_tarifaire">)
        const plagesHoraires =
            getSourceById.value?.informations?.pdl?.comptage?.plages_horaires

        const pricer_global: ComparatorInputPricerGlobal = {
            comptage: {
                plages_horaires: {
                    heures_creuses: plagesHoraires?.heures_creuses ?? [],
                    heures_pointe_fixe:
                        plagesHoraires?.heures_pointe_fixe ?? [],
                },
            },
            structure_tarifaire: {
                offre_tarifaire: {
                    turpe: {
                        inclus_consommation: false,
                        date: moment().format("YYYY-MM-DD"),
                    },
                    cee: {
                        coefficient_consommation_elligible: 1,
                    },
                    oc: {
                        coefficient_securite_pp1: ceeSecurite.value,
                    },
                },
                formule_tarifaire_acheminement: {
                    fta:
                        getSourceById.value?.informations?.pdl
                            ?.structure_tarifaire
                            ?.formule_tarifaire_acheminement?.fta ?? "",
                },
                puissance_souscrite_soutirage: {
                    puissance:
                        getSourceById.value?.informations?.pdl
                            ?.structure_tarifaire?.puissance_souscrite_soutirage
                            ?.puissance ?? null,
                    denivele: getDenivelePuissanceFormated.value,
                },
            },
        }

        const periods =
            modePeriod.value === "custom"
                ? periodSelected.value
                : bestPeriodAvailable.value

        // Compose params with correct types
        const params = {
            source: {
                _id_source: sourceSelected.value,
            },
            data: {
                date_from: moment(periods?.date_from).format("YYYY-MM-DD"),
                date_to: moment(periods?.date_to).format("YYYY-MM-DD"),
                energieactive_elecsoutirage_reference:
                    useEnergieparposteElec.value
                        ? consommationByPlageFournisseur.value
                        : undefined,
            },
            pricer_global,
            offre_actuelle: {
                ...offreActuelle.value,
                _id_supplier:
                    offreActuelle.value?._id_supplier !== "other"
                        ? offreActuelle.value?._id_supplier
                        : null,
            },
            offres_comparees: offresComparees.value.map((offre) => {
                // Crée une snapshot des idperiode pour vérifier les doublons sans modifier l'original
                const snapshotArrayIdPeriode = offre.periodes.map(
                    (periode) => periode.idperiode
                )
                const periodes = offre.periodes.map((periode, index) => {
                    // On vérifie si le idperiode est unique et dans le cas contraire on ajoute un "- A|B|C" à la fin
                    const name = periode.idperiode
                    // Compte le nombre d'occurrences du même idperiode
                    const duplicateCount = snapshotArrayIdPeriode.filter(
                        (p) => p === name
                    ).length

                    // Index de la période parmi les périodes du même nom
                    const duplicateIndex =
                        snapshotArrayIdPeriode
                            // Ne garde que les périodes jusqu'à l'index courant
                            .slice(0, index + 1)
                            .filter((p) => p === name).length - 1

                    if (duplicateCount > 1) {
                        periode.idperiode = `${name}.${String.fromCharCode(65 + duplicateIndex)}`
                    }

                    return periode
                })

                return {
                    ...offre,
                    periodes,
                    _id_supplier:
                        offre?._id_supplier !== "other"
                            ? offre?._id_supplier
                            : null,
                }
            }),
        } as ComparatorInput

        return params
    }

    /**
     * Récupère les sources avec l'API DAPI
     */
    const {
        fetch: fetchSourceByIds,
        isFetching: isFetchingSources,
        hasError: hasErrorSources,
        error: errorSources,
    } = useFetchWrapper(
        async (sourceIds: string[], controller?: AbortController) => {
            if (!services?.$getSourcesInformations) {
                throw new Error(
                    "Service $getSourcesInformations non disponible"
                )
            }

            try {
                sources.value = await services.$getSourcesInformations(
                    sourceIds,
                    controller
                )
            } catch (error) {
                console.error(
                    "Erreur lors de la récupération des sources:",
                    error
                )
                throw error
            }
        }
    )

    /**
     * Récupère le code NAF de l'entreprise titulaire du compteur sélectionné
     * Dépendant de sourceSelected et si la source a un consentement signé
     * par une entreprise avec un idtype = 'siren' et idcompany
     */
    const {
        fetch: fetchNafCodeBySIREN,
        isFetching: isFetchingNafCode,
        hasError: hasErrorNafCode,
        error: errorNafCode,
    } = useFetchWrapper(async (controller: AbortController) => {
        const source = getSourceById.value

        const company =
            source?.informations?.pdl?.consentement?.titulaire?.company

        if (company?.type !== "entreprise" || !company?.idcompany) {
            return
        }

        if (!services?.$getCompanyBySiren) {
            throw new Error("Service $getCompanyBySiren non disponible")
        }

        try {
            const response = await services.$getCompanyBySiren(
                company?.idcompany,
                controller
            )
            codeNaf.value = response.naf_code || ""
        } catch (error) {
            console.error("Erreur lors de la récupération du code NAF:", error)
            throw error
        }
    })

    /**
     * Récupère les périodes valides avec l'API Courtage
     */
    const {
        fetch: fetchPeriodAvailable,
        isFetching: isFetchingPeriods,
        hasError: hasErrorPeriods,
        error: errorPeriods,
    } = useFetchWrapper(async (controller?: AbortController) => {
        if (!sourceSelected.value) {
            throw new Error("Aucune source sélectionnée")
        }

        if (!services?.$getPeriodAvailable) {
            throw new Error("Service $getPeriodAvailable non disponible")
        }

        try {
            const {
                data: { availability },
            } = await services.$getPeriodAvailable(
                sourceSelected.value,
                controller
            )
            periodsAvailable.value = availability.periodes || []
            bestPeriodAvailable.value = availability.best_periode || null

            if (!bestPeriodAvailable.value && periodsAvailable.value.length) {
                throw new ServiceError({
                    message: "Aucune meilleure période disponible",
                    code: "no_best_period",
                })
            }
        } catch (error) {
            periodsAvailable.value = []
            bestPeriodAvailable.value = null

            console.error("Erreur lors de la récupération des périodes:", error)
            throw error
        }
    })

    /**
     * Récupère les données de consommation totale annuelle et les éléments pour l'obligation de capacité
     */
    const {
        fetch: fetchDataPeriode,
        isFetching: isFetchingDataPeriode,
        hasError: hasErrorDataPeriode,
        error: errorDataPeriode,
    } = useFetchWrapper(async (controller?: AbortController) => {
        if (!sourceSelected.value) {
            throw new Error("Aucune source sélectionnée")
        }

        if (!services?.$getDataPeriode) {
            throw new Error("Service $getDataPeriode non disponible")
        }

        const dateTo =
            getSourceById.value?.informations?.pdl?.data?.soutirage?.date_to

        const date_to_string = moment(dateTo).format("YYYY-MM-DD")

        const date_from_string = moment(dateTo)
            .subtract(1, "year")
            .format("YYYY-MM-DD")

        const body = {
            data: {
                date_from: date_from_string,
                date_to: date_to_string,
            },
            source: {
                _id_source: sourceSelected.value,
            },
            categorisation: {
                comptage: {
                    plages_horaires: {
                        heures_creuses:
                            getSourceById.value?.informations?.pdl?.comptage
                                ?.plages_horaires?.heures_creuses ?? [],
                        heures_pointe_fixe:
                            getSourceById.value?.informations?.pdl?.comptage
                                ?.plages_horaires?.heures_pointe_fixe ?? [],
                    },
                },
                structure_tarifaire: {
                    formule_tarifaire_acheminement: {
                        fta:
                            getSourceById.value?.informations?.pdl
                                ?.structure_tarifaire
                                ?.formule_tarifaire_acheminement?.fta ?? "",
                    },
                    puissance_souscrite_soutirage: {
                        puissance:
                            getSourceById.value?.informations?.pdl
                                ?.structure_tarifaire
                                ?.puissance_souscrite_soutirage?.puissance ??
                            null,
                        denivele: getDenivelePuissanceFormated.value,
                    },
                    offre_tarifaire: {
                        option_tarifaire: getCurrentOptionTarifaire.value,
                    },
                },
            },
        } as ComparatorInputPricerGetData

        try {
            const { data } = await services.$getDataPeriode(body, controller)

            consommationTotalAnnuelle.value =
                data?.periode?.energieactive_elecsoutirage_tout?.sum || 0

            consommationCee.value = consommationTotalAnnuelle.value
        } catch (error) {
            console.error(
                "Erreur lors de la récupération des données période:",
                error
            )
            consommationTotalAnnuelle.value = null

            throw error
        }
    })

    const {
        fetch: fetchConsoParPlage,
        isFetching: isFetchingConsoParPlage,
        hasError: hasErrorConsoParPlage,
        error: errorConsoParPlage,
    } = useFetchWrapper(
        async (
            {
                offer,
                callback,
                callbackError,
            }: {
                offer: OptionTarifaire
                callback?: (data: Record<string, number>) => void
                callbackError?: () => void
            } = {
                offer: OptionTarifaire.Base,
                callback: undefined,
                callbackError: undefined,
            },
            controller?: AbortController
        ) => {
            if (!getSourceById.value) {
                throw new Error("Aucune source sélectionnée")
            }

            if (!services?.$getDataPeriode) {
                throw new Error("Service $getDataPeriode non disponible")
            }

            const dateTo =
                getSourceById.value?.informations?.pdl?.data?.soutirage?.date_to

            const date_to_string = moment(dateTo).format("YYYY-MM-DD")

            const date_from_string = moment(dateTo)
                .subtract(1, "year")
                .format("YYYY-MM-DD")

            const body = {
                data: {
                    date_from: date_from_string,
                    date_to: date_to_string,
                },
                source: {
                    _id_source: getSourceById.value._id_source,
                },
                categorisation: {
                    comptage: {
                        plages_horaires: {
                            heures_creuses:
                                getSourceById.value?.informations?.pdl?.comptage
                                    ?.plages_horaires?.heures_creuses ?? [],
                            heures_pointe_fixe:
                                getSourceById.value?.informations?.pdl?.comptage
                                    ?.plages_horaires?.heures_pointe_fixe ?? [],
                        },
                    },
                    structure_tarifaire: {
                        formule_tarifaire_acheminement: {
                            fta:
                                getSourceById.value?.informations?.pdl
                                    ?.structure_tarifaire
                                    ?.formule_tarifaire_acheminement?.fta ?? "",
                        },
                        puissance_souscrite_soutirage: {
                            puissance:
                                getSourceById.value?.informations?.pdl
                                    ?.structure_tarifaire
                                    ?.puissance_souscrite_soutirage
                                    ?.puissance ?? null,
                            denivele: getDenivelePuissanceFormated.value,
                        },
                        offre_tarifaire: {
                            option_tarifaire: offer,
                        },
                    },
                },
            } as ComparatorInputPricerGetData

            try {
                const { data } = await services.$getDataPeriode(
                    body,
                    controller
                )

                const dataFormated = data.plages.fournisseur.reduce(
                    (acc, plage) => {
                        acc[plage.plage_elecsoutirage_fournisseur.nostat] =
                            plage.energieactive_elecsoutirage_tout.sum
                        return acc
                    },
                    {} as { [name: string]: number }
                )

                // Mise à jour des données de consommation par plage
                consommationByPlageFournisseur.value = dataFormated
                snapshotConsommationByPlageFournisseur.value = {
                    ...dataFormated,
                }

                if (callback) {
                    callback(dataFormated)
                }
            } catch (error) {
                console.error(
                    "Erreur lors de la récupération des données période:",
                    error
                )

                if (callbackError) {
                    callbackError()
                }

                throw error
            }
        }
    )

    /**
     * Lance la comparaison des offres
     * @param analyse - Indique si on compare en sauvegardant l'analyse
     */
    const {
        fetch: createCompareOffers,
        isFetching: isFetchingCompareOffers,
        hasError: hasErrorCompareOffers,
        error: errorCompareOffers,
    } = useFetchWrapper(async (controller?: AbortController) => {
        if (!services?.$createCompareOffers) {
            throw new Error("Service $createCompareOffers non disponible")
        }

        const params = getBodyInputCompareOffers()

        try {
            const data = await services.$createCompareOffers(params, controller)
            currentAnalysis.value = data.data
            warnings.value = (data.warnings || []).reduce(
                (acc: ComparatorOutputWarning[], warning) => {
                    // Évite les doublons de warnings
                    if (!acc.find((w) => w.code === warning.code)) {
                        acc.push(warning)
                    }
                    return acc
                },
                []
            )
        } catch (error) {
            console.error(
                "Erreur lors de la création de la comparaison d'offres:",
                error
            )
            throw error
        }
    })

    /**
     * Lance la comparaison des offres
     * @param analyse - Indique si on compare en sauvegardant l'analyse
     */
    const {
        fetch: createCompareOffersAnalysis,
        isFetching: isFetchingCompareOffersAnalysis,
        hasError: hasErrorCompareOffersAnalysis,
        error: errorCompareOffersAnalysis,
    } = useFetchWrapper(
        async (
            {
                name,
                callback,
            }: {
                name: string
                callback?: (analysisId: string) => void
            } = {
                name: "",
                callback: undefined,
            },
            controller?: AbortController
        ) => {
            if (!services?.$createCompareOffersAnalysis) {
                throw new Error("Service $createCompareOffers non disponible")
            }

            const inputs = getBodyInputCompareOffers()

            try {
                const analysis = await services.$createCompareOffersAnalysis(
                    inputs,
                    {
                        name: name || "",
                        _id_building: buildingSelected.value?._id ?? "",
                        _id_company: companyId.value ?? "",
                        _id_user: userId.value ?? "",
                        _id_source: sourceSelected.value ?? "",
                    },
                    controller
                )

                // Si l'analyse existe déjà, on la remplace
                const index = analyses.value.findIndex(
                    (a) => a._id === analysis._id
                )
                if (index !== -1) {
                    analyses.value[index] = analysis
                } else {
                    analyses.value.push(analysis)
                }
                if (callback) {
                    callback(analysis._id)
                }
            } catch (error) {
                console.error(
                    "Erreur lors de la création de la comparaison d'offres:",
                    error
                )
                throw error
            }
        }
    )

    /**
     * Récupère toutes les analyses de comparaison d'offres pour un bâtiment
     */
    const {
        fetch: fetchAllAnalysesComparatorOffers,
        isFetching: isFetchingAllAnalysesComparatorOffers,
        hasError: hasErrorAllAnalysesComparatorOffers,
        error: errorAllAnalysesComparatorOffers,
    } = useFetchWrapper(async (controller?: AbortController) => {
        if (!services?.$getAllAnalysesComparatorOffers) {
            throw new Error(
                "Service $getAllAnalysesComparatorOffers non disponible"
            )
        }

        try {
            const analysesResponse =
                await services.$getAllAnalysesComparatorOffers(
                    buildingSelected.value?._id ?? "",
                    controller
                )
            analyses.value = analysesResponse
        } catch (error) {
            console.error("Erreur lors de la récupération de l'analyse:", error)
            throw error
        }
    })

    const {
        fetch: deleteCompareOffersAnalysis,
        isFetching: isFetchingDeleteCompareOffersAnalysis,
        hasError: hasErrorDeleteCompareOffersAnalysis,
        error: errorDeleteCompareOffersAnalysis,
    } = useFetchWrapper(
        async (analysisId: string, controller?: AbortController) => {
            if (!services?.$deleteCompareOffersAnalysis) {
                throw new Error(
                    "Service $deleteCompareOffersAnalysis non disponible"
                )
            }
            try {
                await services.$deleteCompareOffersAnalysis(
                    analysisId,
                    controller
                )
                // Supprime l'analyse du store
                const index = analyses.value.findIndex(
                    (a) => (a?._id || a?._id_analysis) === analysisId
                )
                if (index !== -1) {
                    analyses.value.splice(index, 1)
                }
            } catch (error) {
                console.error(
                    "Erreur lors de la suppression de l'analyse:",
                    error
                )
                throw error
            }
        }
    )

    /**
     * Récupère les fournisseurs d'énergies
     */
    const {
        fetch: fetchSuppliers,
        isFetching: isFetchingSuppliers,
        hasError: hasErrorSuppliers,
        error: errorSuppliers,
    } = useFetchWrapper(async (controller?: AbortController) => {
        if (!services?.$getSuppliers) {
            throw new Error("Service $getSuppliers non disponible")
        }

        try {
            suppliers.value = await services.$getSuppliers(controller)

            // Trie les fournisseurs par nom
            suppliers.value.sort((a, b) => a.name.localeCompare(b.name))

            // Ajoute un fournisseur par défaut
            suppliers.value.push({
                name: "Autre",
                _id_supplier: "other",
                img: "",
                website_url: "",
            })
        } catch (error) {
            console.error(
                "Erreur lors de la récupération des fournisseurs:",
                error
            )
            throw error
        }
    })

    const {
        fetch: fetchSourceChart,
        isFetching: isFetchingSourceChart,
        hasError: hasErrorSourceChart,
        error: errorSourceChart,
    } = useFetchWrapper(async (controller?: AbortController) => {
        if (!services?.$createGraphBySourceId) {
            throw new Error("Service $createGraphBySourceId non disponible")
        }
        const date_to = moment().format("YYYY-MM-DD")
        const date_from = moment().subtract(1, "year").format("YYYY-MM-DD")

        const _id_source = getSourceById.value?._id_source ?? ""
        // ajoute un délai aléatoire entre 1 et 5 secondes pour simuler un chargement
        try {
            const { data } = await services.$createGraphBySourceId(
                _id_source,
                date_from,
                date_to,
                "hourly",
                [
                    {
                        render_mode: "source",
                        type: "energie_elec_tout.sum",
                        unit: "kWh",
                        name: "Consommation électrique",
                        icon: "pi pi-chart-bar",
                        graph_type: "bar",
                        alarm: false,
                        icon_color: "#3B82F6",
                    },
                ],
                {
                    ylabel: "Consommation (kWh)",
                    abordController: controller,
                }
            )
            graphJson.value = {
                _id_source,
                json: JSON.parse(data),
            }
        } catch (error) {
            console.error("Erreur lors de la récupération du graphique:", error)
            throw error
        }
    })

    /**
     * Récupère un rapport PDF de son analyse de comparaison d'offres par son identifiant
     */
    const {
        fetch: fetchCompareOffersExportByAnalyseId,
        isFetching: isFetchingAnalysisExport,
        hasError: hasErrorAnalysisExport,
        error: errorAnalysisExport,
    } = useFetchWrapper(
        async ({ analysisId, callback }, controller?: AbortController) => {
            if (!services?.$getCompareOffersExportAnalysisById) {
                throw new Error(
                    "Service $getCompareOffersExportAnalysisById non disponible"
                )
            }

            try {
                const url = await services.$getCompareOffersExportAnalysisById(
                    analysisId,
                    controller
                )
                if (callback) {
                    callback(url)
                }
            } catch (error) {
                console.error(
                    "Erreur lors de la récupération de l'analyse:",
                    error
                )
                throw error
            }
        }
    )

    // ==================
    // EXPORTS
    // ==================

    return {
        // State
        sources,
        sourceSelected,
        ceeSecurite,
        consommationCee,
        consommationTotalAnnuelle,
        codeNaf,
        periodsAvailable,
        currentAnalysis,
        suppliers,
        periodSelected,
        bestPeriodAvailable,
        modePeriod,
        graphJson,
        analyses,
        offreActuelle,
        offresComparees,
        consommationByPlageFournisseur,
        snapshotConsommationByPlageFournisseur,
        useEnergieparposteElec,
        warnings,

        // Getters
        getSourceById,
        getPuissanceOc,
        getWorkflowEventsBySourceId,
        getSourcesWithEligible,
        getCurrentOptionTarifaire,
        getDenivelePuissanceFormated,
        getDenivelePuissance,

        // Actions synchrones
        $reset,

        // Actions asynchrones
        fetchSourceByIds,
        fetchNafCodeBySIREN,
        fetchPeriodAvailable,
        fetchDataPeriode,
        createCompareOffers,
        createCompareOffersAnalysis,
        fetchSuppliers,
        fetchSourceChart,
        fetchAllAnalysesComparatorOffers,
        deleteCompareOffersAnalysis,
        fetchCompareOffersExportByAnalyseId,
        fetchConsoParPlage,

        // États de chargement
        isFetchingSources,
        isFetchingNafCode,
        isFetchingPeriods,
        isFetchingDataPeriode,
        isFetchingCompareOffers,
        isFetchingCompareOffersAnalysis,
        isFetchingSuppliers,
        isFetchingSourceChart,
        isFetchingAllAnalysesComparatorOffers,
        isFetchingDeleteCompareOffersAnalysis,
        isFetchingAnalysisExport,
        isFetchingConsoParPlage,

        // États d'erreur
        hasErrorSources,
        hasErrorNafCode,
        hasErrorPeriods,
        hasErrorDataPeriode,
        hasErrorCompareOffers,
        hasErrorCompareOffersAnalysis,
        hasErrorSuppliers,
        hasErrorSourceChart,
        hasErrorAllAnalysesComparatorOffers,
        hasErrorDeleteCompareOffersAnalysis,
        hasErrorAnalysisExport,
        hasErrorConsoParPlage,

        // Erreurs
        errorSources,
        errorNafCode,
        errorPeriods,
        errorDataPeriode,
        errorCompareOffers,
        errorCompareOffersAnalysis,
        errorSuppliers,
        errorSourceChart,
        errorAllAnalysesComparatorOffers,
        errorDeleteCompareOffersAnalysis,
        errorAnalysisExport,
        errorConsoParPlage,
    }
})
